import { forwardRef, HTMLAttributes } from 'react';
import { getIconClass, ICONS, type IconName } from '@/constants/icons';

/**
 * Icon size variants
 */
type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';

/**
 * Icon size to inline style mapping (responsive via clamp + vh)
 * Scales icons with viewport height for short screens (e.g., 1366×768 laptops)
 */
const SIZE_STYLES: Record<Exclude<IconSize, 'custom'>, string> = {
  xs: 'clamp(10px, 1.3vh, 12px)',   // 12px max - for tiny icons
  sm: 'clamp(13px, 1.8vh, 16px)',   // 16px max
  md: 'clamp(18px, 2.5vh, 24px)',   // 24px max
  lg: 'clamp(24px, 3.5vh, 32px)',   // 32px max
  xl: 'clamp(36px, 5vh, 48px)',     // 48px max
};

/**
 * Icon component props
 */
export interface IconProps extends Omit<HTMLAttributes<HTMLElement>, 'className'> {
  /**
   * Icon name (with or without 'hm-' prefix)
   * @example 'dashboard' | 'hm-dashboard' | ICONS.DASHBOARD
   */
  name: IconName | undefined;

  /**
   * Icon size
   * - xs: 10–12px (responsive) - tiny icons
   * - sm: 13–16px (responsive)
   * - md: 18–24px (responsive) - Default
   * - lg: 24–32px (responsive)
   * - xl: 36–48px (responsive)
   * - custom: Use className prop for custom sizing
   * @default 'md'
   */
  size?: IconSize;

  /**
   * Tailwind color class
   * @example 'text-primary-500' | 'text-gray-600'
   */
  color?: string;

  /**
   * Additional CSS classes
   * Use this for custom sizing when size='custom'
   */
  className?: string;

  /**
   * Accessible label for screen readers
   * If provided, aria-hidden will be set to false
   */
  ariaLabel?: string;

  /**
   * Hide icon from screen readers
   * Set to false if icon is decorative only
   * @default true (if no ariaLabel provided)
   */
  ariaHidden?: boolean;
}

/**
 * Icon Component
 *
 * Renders a Happy Icons font icon with consistent styling and accessibility
 *
 * @example
 * // Basic usage
 * <Icon name="dashboard" />
 * <Icon name={ICONS.DASHBOARD} />
 *
 * @example
 * // With size
 * <Icon name="search" size="sm" />
 * <Icon name="plus" size="lg" />
 *
 * @example
 * // With color
 * <Icon name="heart" color="text-red-500" />
 * <Icon name="star" color="text-yellow-400" />
 *
 * @example
 * // With accessibility
 * <Icon name="search" ariaLabel="Search posts" />
 * <Icon name="close" ariaLabel="Close modal" />
 *
 * @example
 * // Custom sizing
 * <Icon name="logo" size="custom" className="text-[64px]" />
 *
 * @example
 * // Combined props
 * <Icon
 *   name="trash"
 *   size="sm"
 *   color="text-red-600"
 *   className="hover:text-red-800 cursor-pointer"
 *   ariaLabel="Delete post"
 * />
 */
export const Icon = forwardRef<HTMLElement, IconProps>(({
  name,
  size = 'md',
  color,
  className = '',
  ariaLabel,
  ariaHidden,
  ...props
}, ref) => {
  // Get base icon classes
  const isReleaseStamp = name === ICONS.RELEASE_STAMP;
  const iconClass = isReleaseStamp ? 'material-symbols-line' : getIconClass(name);

  // Build size style (responsive clamp) or skip for custom
  const sizeStyle = size === 'custom' ? undefined : { fontSize: SIZE_STYLES[size] };

  // Combine all classes (no size class — handled by inline style)
  const combinedClassName = [
    iconClass,
    color,
    'leading-none', // Prevent extra vertical space
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Determine aria-hidden value
  const shouldHideFromScreenReaders =
    ariaHidden !== undefined ? ariaHidden : !ariaLabel;

  return (
    <i
      ref={ref}
      className={combinedClassName}
      style={sizeStyle}
      aria-label={ariaLabel}
      aria-hidden={shouldHideFromScreenReaders}
      {...props}
    >{isReleaseStamp ? 'approval' : null}</i>
  );
});

Icon.displayName = 'Icon';

/**
 * Icon Button Wrapper
 * Convenience component for clickable icons
 *
 * @example
 * <IconButton
 *   icon="trash"
 *   ariaLabel="Delete post"
 *   onClick={handleDelete}
 * />
 */
export interface IconButtonProps extends IconProps {
  onClick?: () => void;
  disabled?: boolean;
}

export const IconButton = ({
  onClick,
  disabled = false,
  className = '',
  ...iconProps
}: IconButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      aria-label={iconProps.ariaLabel}
    >
      <Icon {...iconProps} />
    </button>
  );
};
